import {
  CatalogData, Material,
  MaterialCategory, ProductData, VariantData,
} from "ecom-types";

interface ProductMaterialsWithCategories extends MaterialCategory {
  materials: Material[];
}

export const useProduct = (
  product: CatalogData | ProductData | null,
  sizesSlugs: string[]
) => {
  const localePath = useLocalePathPolyfill();
  const generalStore = useGeneralStore();


  const productInfo = ref<ProductData | CatalogData | null>(product);
  const configurationInfo = ref<VariantData | null>(null);
  const dynamicProductTitle = ref("");

  const productTitle = computed<string>(() => {
    return (
      dynamicProductTitle.value ||
      configurationInfo.value?.currentTranslation?.title ||
      (productInfo.value as CatalogData)?.selected_config?.currentTranslation?.title ||
      (productInfo.value as CatalogData)?.variant_plp_image?.currentTranslation
        ?.title ||
      (productInfo.value as CatalogData)?.default_config?.currentTranslation
        ?.title ||
      productInfo.value?.currentTranslation?.title ||
      ""
    );
  });

  const productTitlePlp = computed<string>(() => {
    return productInfo.value?.currentTranslation?.title || "";
  });

  const productDescription = computed(() => {
    return (
      configurationInfo.value?.currentTranslation?.description ||
      (productInfo.value as CatalogData)?.selected_config?.currentTranslation?.description ||
      (productInfo.value as CatalogData)?.variant_plp_image?.currentTranslation
        ?.description ||
      (productInfo.value as CatalogData)?.default_config?.currentTranslation
        ?.description ||
      (productInfo.value as ProductData)?.currentTranslation
        ?.long_description ||
      ""
    );
  });

  const productMaterials = computed(() => {
    return productInfo.value?.material_categories_plp_hover_ids?.map((el) => {
      const materialCategory =(generalStore.bootstrap?.material_categories)?.find(
        (category) => category.id === el,
      );

      const materials = (productInfo.value?.materials as Material[]) || [];
      return {
        ...materialCategory,
        materials: materials.reduce((acc: Material[], material: Material) => {
          if (material.material_category_id === el) {
            acc.push({
              ...material,
            });
          }
          return acc;
        }, []),
      };
    }) as ProductMaterialsWithCategories[];
  });

  const productPrice = computed(() => {
    return (
      Number(
        configurationInfo.value?.cost ||
          productInfo.value?.selected_config?.cost ||
          (productInfo.value as CatalogData)?.variant_plp_image?.cost ||
          productInfo.value?.default_config?.cost ||
          productInfo.value?.min_cost ||
          productInfo.value?.cost ||
          0,
      )
    );
  });


  const productStartAtPrice = computed(() => {
    return (
        Number(configurationInfo.value?.start_at_cost || 0) ||
        Number(productInfo.value?.selected_config?.start_at_cost || 0) ||
        Number(
            (productInfo.value as CatalogData)?.variant_plp_image?.start_at_cost ||
            0,
        ) ||
        Number(productInfo.value?.default_config?.start_at_cost || 0) ||
        Number(productInfo.value?.start_at_cost || 0) ||
        0
    );
  });

  const configurationPrice = computed(() => {
    return Number(configurationInfo.value?.cost || 0);
  });

  const configurationStartAtPrice = computed(() => {
    return Number(configurationInfo.value?.start_at_cost || 0);
  });

  const productSKU = computed(() => {
    return (
      productInfo.value?.selected_config?.configuration_id ||
      (productInfo.value as CatalogData)?.variant_plp_image?.sku ||
      productInfo.value?.default_config?.configuration_id ||
      productInfo.value?.default_sku
    );
  });

  const discountValue = computed(() => {
    if (
      !productInfo.value?.sale ||
      !productInfo.value?.sale_value ||
      !productPrice.value
    ) {
      return 0;
    }

    return Number(
      (
        productPrice.value -
        productPrice.value * (+productInfo.value.sale_value / 100)
      ).toFixed(2),
    );
  });

  const configurationDiscountValue = computed(() => {
    if (
      !productInfo.value?.sale ||
      !productInfo.value?.sale_value ||
      !configurationPrice.value
    ) {
      return 0;
    }

    return Number(
      (
        configurationPrice.value -
        configurationPrice.value * (+productInfo.value.sale_value / 100)
      ).toFixed(2),
    );
  });

  const productSizeDefaultQuery = computed(() => {
    const sizeDefaultQuery: { size?: string } = {};
    if (
        productInfo.value?.material_categories?.some((item) =>
            sizesSlugs.includes(item.slug),
        )
    ) {
      sizeDefaultQuery.size = "default";
    }
    return sizeDefaultQuery;
  })

  const productLink = computed(() => {
    return localePath({
          path: `/product/${productInfo.value?.slug}`,
          query: {
            configuration_id: productSKU.value,
            ...productSizeDefaultQuery.value,
          },
        });
  });

  const finalProductPriceValue = computed(() => {
    return discountValue.value || productPrice.value || 0;
  });

  const finalConfigurationPriceValue = computed(() => {
    return configurationDiscountValue.value || configurationPrice.value || 0;
  });

  const materialCategoriesDictionary = computed(() => {
    return (
      productInfo.value?.material_categories?.reduce(
        (acc, el) => {
          acc[el.sku] = el.slug;
          return acc;
        },
        {} as Record<string, string>,
      ) || {}
    );
  });

  const isConfigurationOutOfStock = computed(() => {
    const configurationOutOfStock =
        configurationInfo.value?.track_inventory &&
        !configurationInfo.value?.stock_qty;
    return Boolean(
        configurationOutOfStock || !finalConfigurationPriceValue.value,
    );
  });

  const isConfigurationInventoryOutOfStockPlp = computed(() => {
    if (productInfo.value?.selected_config) {
      return Boolean(
        !productInfo.value?.selected_config?.stock_qty &&
        productInfo.value?.selected_config?.track_inventory,
      );
    }
    if ((productInfo.value as CatalogData)?.variant_plp_image) {
      return Boolean(
          !(productInfo.value as CatalogData)?.variant_plp_image?.stock_qty &&
          (productInfo.value as CatalogData)?.variant_plp_image
              ?.track_inventory,
      );
    } else if ((productInfo.value as CatalogData)?.default_config) {
      return Boolean(
          !(productInfo.value as CatalogData)?.default_config?.stock_qty &&
          (productInfo.value as CatalogData)?.default_config?.track_inventory,
      );
    } else {
      return false;
    }
  });

  const isConfigurationOutOfStockPlp = computed(() => {
    return !productPrice.value || isConfigurationInventoryOutOfStockPlp.value;
  })

  return {
    productMaterials,
    productLink,
    productPrice,
    productSKU,
    productTitle,
    productDescription,
    productInfo,
    configurationInfo,
    discountValue,
    finalProductPriceValue,
    materialCategoriesDictionary,
    dynamicProductTitle,
    productTitlePlp,
    configurationPrice,
    configurationDiscountValue,
    finalConfigurationPriceValue,
    productSizeDefaultQuery,
    productStartAtPrice,
    configurationStartAtPrice,
    isConfigurationOutOfStock,
    isConfigurationInventoryOutOfStockPlp,
    isConfigurationOutOfStockPlp
  };
};
